'use client';

import { useState, FormEvent, ChangeEvent, useRef } from 'react';
import { Turnstile, TurnstileInstance } from '@marsidev/react-turnstile';
import { postStrapi } from '@/lib/api';

/**
 * Controlled input values for the course consultation inquiry form.
 */
interface FormFields {
  [key: string]: string;
  /** Participant's given name */
  firstName: string;
  /** Participant's family name */
  lastName: string;
  /** Contact email address */
  email: string;
  /** Mobile telephone number */
  phone: string;
}

/**
 * UI feedback and asynchronous submission state.
 */
interface StatusState {
  /** Indicates whether an active network request is in flight */
  loading: boolean;
  /** User-friendly success confirmation message */
  success?: string;
  /** Error message detailing validation or API submission failure */
  error?: string;
}

/**
 * Lead capture consultation form embedded in course category banners.
 *
 * Collects prospective student contact info, validates fields client-side,
 * enforces bot protection via Cloudflare Turnstile, and posts the lead to Strapi.
 *
 * @returns Interactive consultation inquiry form JSX element
 */
export default function CourseBannerForm() {
  const turnstileRef = useRef<TurnstileInstance>(null);
  const [turnstileToken, setTurnstileToken] = useState<string>('');

  const [formData, setFormData] = useState<FormFields>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });

  const [status, setStatus] = useState<StatusState>({ loading: false });

  /**
   * Validates name completeness, email structure, and phone number syntax.
   *
   * @returns Error message string if invalid, or `null` if all validations pass
   */
  const validateForm = (): string | null => {
    if (!formData.firstName.trim()) return 'First name is required.';
    if (!formData.lastName.trim()) return 'Last name is required.';
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email.trim())) return 'Please enter a valid email address.';

    const phoneRegex = /^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s./0-9]*$/;
    if (!formData.phone.trim() || !phoneRegex.test(formData.phone.trim())) {
      return 'Please enter a valid phone number.';
    }

    return null;
  };

  /**
   * Updates state on change for controlled form inputs.
   *
   * @param e - Input change event containing field name and value
   */
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  /**
   * Handles form submission, bot verification, and posting to Strapi.
   *
   * @param e - React synthetic form submit event
   */
  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {

    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      setStatus({ loading: false, error: validationError });
      return;
    }

    const activeToken = turnstileToken || turnstileRef.current?.getResponse();
    if (!activeToken) {
      setStatus({ 
        loading: false, 
        error: 'Security verification in progress. Please try again in a moment.' 
      });
      return;
    }

    setStatus({ loading: true });

    try {
      await postStrapi('/form-submissions', {
        data: formData as Record<string, unknown>,
        turnstileToken: activeToken,
      });

      setStatus({
        loading: false,
        success: 'Thank you! Your request has been submitted successfully.',
      });

      setFormData({ firstName: '', lastName: '', email: '', phone: '' });
      setTurnstileToken('');
      turnstileRef.current?.reset();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to submit. Please try again.';
      setStatus({
        loading: false,
        error: errorMessage,
      });
      setTurnstileToken('');
      turnstileRef.current?.reset();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="row subscription-row">
      <div className="form-wrapper xdomain">
        <div className="form-field-group">
          <input 
            type="text" 
            name="firstName"
            placeholder="First Name" 
            value={formData.firstName}
            onChange={handleChange}
          />
          <input 
            type="text" 
            name="lastName"
            placeholder="Last Name" 
            value={formData.lastName}
            onChange={handleChange}
          />
        </div>
        <div className="form-field-group">
          <input 
            type="email" 
            name="email"
            placeholder="Email Address" 
            value={formData.email}
            onChange={handleChange}
          />
          <input 
            type="tel" 
            name="phone"
            placeholder="Phone Number" 
            value={formData.phone}
            onChange={handleChange}
          />
        </div>
      </div>

      <Turnstile
        ref={turnstileRef}
        siteKey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || ''}
        onSuccess={(t: string) => setTurnstileToken(t)}
        onExpire={() => setTurnstileToken('')}
        options={{
          action: 'submit-consultation',
          theme: 'light',
        }}
      />

      <input 
        type="submit" 
        className="text-white" 
        value={status.loading ? "Submitting..." : "Get My Funding Consultation Today"} 
        disabled={status.loading}
      />

      {status.success && (
        <div style={{ marginTop: '12px', padding: '10px 14px', backgroundColor: '#d4edda', color: '#155724', borderRadius: '4px', fontSize: '14px', width: '100%' }}>
          {status.success}
        </div>
      )}

      {status.error && (
        <div style={{ marginTop: '12px', padding: '10px 14px', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '4px', fontSize: '14px', width: '100%' }}>
          {status.error}
        </div>
      )}

      <div className="form-label">
        <div className="icon">
          <img src="/assets/images/lock.svg" alt="Lock Icon" />
        </div>
        <small>Your information is securely handled. We respect your data privacy.</small>
      </div>
    </form>
  );
}
